/**
 * Web Vitals tracking for Core Web Vitals monitoring.
 *
 * Tracks LCP (Largest Contentful Paint), FID (First Input Delay),
 * CLS (Cumulative Layout Shift), TTFB (Time to First Byte),
 * and INP (Interaction to Next Paint).
 */

import { onLCP, onCLS, onTTFB, onINP, type Metric } from 'web-vitals';

// Google's recommended thresholds for "good" performance
// Note: FID is deprecated in web-vitals v4, replaced by INP
const THRESHOLDS: Record<string, number> = {
  LCP: 2500, // 2.5 seconds
  CLS: 0.1, // 0.1 score
  TTFB: 800, // 800ms
  INP: 200, // 200ms (Interaction to Next Paint)
};

/**
 * Log a Web Vital metric to console (dev) or analytics (prod).
 */
const logVital = (metric: Metric): void => {
  const { name, value, rating, id } = metric;

  const data = {
    name,
    value: Math.round(value * 100) / 100,
    rating, // 'good', 'needs-improvement', 'poor'
    id,
    threshold: THRESHOLDS[name],
  };

  if (import.meta.env.DEV) {
    const emoji =
      rating === 'good' ? '\u2705' : rating === 'needs-improvement' ? '\u26A0\uFE0F' : '\u274C';
    console.log(`[Web Vitals] ${emoji} ${name}: ${data.value} (${rating})`);
  }

  // In production, could send to analytics endpoint:
  // navigator.sendBeacon('/api/v2/metrics/vitals', JSON.stringify(data));
};

/**
 * Initialize Web Vitals tracking.
 * Call once in main.tsx after the app mounts.
 *
 * @example
 * ```tsx
 * // In main.tsx, after createRoot().render()
 * initWebVitals();
 * ```
 */
export function initWebVitals(): void {
  onLCP(logVital);
  onCLS(logVital);
  onTTFB(logVital);
  onINP(logVital);

  if (import.meta.env.DEV) {
    console.log('[Web Vitals] Tracking initialized');
  }
}

/**
 * Get navigation timing metrics for detailed page load breakdown.
 * Useful for understanding where time is spent during page load.
 *
 * @returns Navigation timing breakdown or null if unavailable
 */
export function getNavigationTiming(): Record<string, number> | null {
  const entries = performance.getEntriesByType('navigation');
  if (entries.length === 0) return null;

  const nav = entries[0] as PerformanceNavigationTiming;

  return {
    dnsLookup: Math.round(nav.domainLookupEnd - nav.domainLookupStart),
    tcpConnect: Math.round(nav.connectEnd - nav.connectStart),
    ttfb: Math.round(nav.responseStart - nav.requestStart),
    contentDownload: Math.round(nav.responseEnd - nav.responseStart),
    domParsing: Math.round(nav.domContentLoadedEventEnd - nav.responseEnd),
    resourceLoad: Math.round(nav.loadEventEnd - nav.domContentLoadedEventEnd),
    total: Math.round(nav.loadEventEnd - nav.startTime),
  };
}

/**
 * Log current navigation timing to console.
 * Call after page is fully loaded (e.g., in useEffect with empty deps).
 */
export function logNavigationTiming(): void {
  const timing = getNavigationTiming();
  if (timing && import.meta.env.DEV) {
    console.log('[Web Vitals] Navigation Timing:', timing);
  }
}
